import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import { useSwiftCodeLookupQuery } from '@/api/v1';
import { FormCheckbox } from '@/components/atoms/FormCheckbox';
import { AddressFields } from '@/components/organisms/AddressFields';
import { ControllerWithError, ControllerWithErrorParam } from '@/forms/ControllerWithError';
import { CountryCity, useCountryCities } from '@/hooks/useCountryCities';
import { FormWithErrorsRegister } from '@/hooks/useFormWithErrors';
import { useTranslator } from '@/hooks/useTranslator';
import type { paymentMethodFiatSchema } from '@/schemas/paymentMethod';
import { isEmptyNullOrUndefined } from '@/utility/isEmptyNullOrUndefined';

type PaymentMethodForm = typeof paymentMethodFiatSchema.infer;

interface Props {
  readonly register: FormWithErrorsRegister<PaymentMethodForm>;
  readonly control: Control<PaymentMethodForm>;
  readonly watch: UseFormWatch<PaymentMethodForm>;
  readonly setValue: UseFormSetValue<PaymentMethodForm>;
}

export const SwiftAccountDetailsFields: React.FC<Props> = ({
  register,
  watch,
  control,
  setValue,
}: Props): React.ReactElement => {
  const tr = useTranslator();
  const [country, state, swiftCode] = watch(['country', 'address.state', 'swiftCode']);

  const cities = useCountryCities(country);
  const { data: bankData } = useSwiftCodeLookupQuery(swiftCode, {
    skip: isEmptyNullOrUndefined(swiftCode),
  });

  React.useEffect((): void => {
    if (bankData) {
      const { name, address, cityName, postcode } = bankData.data;

      setValue('bankName', name ?? '', { shouldTouch: true });
      setValue(
        'address',
        {
          street: address ?? '',
          state:
            cities.find((city: CountryCity): boolean => {
              const { name } = city;

              return name.toLowerCase() === cityName.toLowerCase();
            })?.state ?? '',
          complement: '',
          city: cityName ?? '',
          zipCode: postcode ?? '',
        },
        { shouldTouch: true },
      );
    }
  }, [bankData, cities, country, setValue]);

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="body2" color="text.primary">
          {tr('BANK_ACCOUNT_INFORMATION')}
        </Typography>
      </Box>
      <Box display="flex" gap={2}>
        <TextField
          fullWidth={true}
          {...register({ name: 'swiftCode', label: tr('SWIFT_CODE') })}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setValue('swiftCode', e.target.value.replace(/\s/g, ''), { shouldTouch: true });
          }}
        />
        <TextField
          fullWidth={true}
          {...register({ name: 'iban', label: tr('IBAN_ACCOUNT_NUMBER') })}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
            setValue('iban', e.target.value.replace(/\s/g, ''), { shouldTouch: true });
          }}
        />
      </Box>
      <ControllerWithError
        control={control}
        name="bankName"
        label={tr('BANK_NAME')}
        render={({
          field,
        }: ControllerWithErrorParam<PaymentMethodForm, 'bankName'>): React.ReactElement => (
          <TextField fullWidth={true} {...field} />
        )}
      />

      <AddressFields<PaymentMethodForm> country={country} state={state} control={control} />

      <FormCheckbox {...register({ name: 'isPobo', label: tr('IS_POBO') })} />
    </Box>
  );
};
